import dashboardReadyIcon from '@/assets/icons/dashboard-ready.svg';
import dashboardNeedsAttentionIcon from '@/assets/icons/dashboard-needs-attention.svg';
import dashboardAwaitingIcon from '@/assets/icons/dashboard-awaiting.svg';
import dashboardEmailIcon from '@/assets/icons/dashboard-email.svg';
import dashboardPendingIcon from '@/assets/icons/dashboard-pending.svg';
import dashboardInProgressIcon from '@/assets/icons/dashboard-in-progress.svg';
import dashboardNotSetIcon from '@/assets/icons/dashboard-not-set.svg';

export enum DashboardStatus {
  DASHBOARD_READY = 'Dashboard Ready',
  NEEDS_ATTENTION = 'Needs Attention',
  AWAITING_DASHBOARD = 'Awaiting Dashboard',
  // Phase 9G (BUG-SCORING-PATH-DIVERGENCE-01): Consented member with no official score yet.
  // Distinct from IN_PROGRESS (onboarding) — member has consented but official score
  // has not been published. Does NOT indicate poor financial health.
  AWAITING_SCORE = 'Awaiting Score',
  PENDING = 'Pending',
  EMAIL_NOT_CONFIRMED = 'Email Not Confirmed',
  IN_PROGRESS = 'In Progress',
  INVITED = 'Invited',
}

export interface StatusMetadata {
  icon: string;
  color: string;
  bgColor: string;
  iconBgColor: string;
  description: string;
  requiresAction: boolean;
  priority: number; // Higher number = higher priority
}

export const STATUS_METADATA: Record<DashboardStatus | 'Not Set', StatusMetadata> = {
  // Phase 9G: Awaiting Score — neutral, non-alarming status for members without official score
  [DashboardStatus.AWAITING_SCORE]: {
    icon: dashboardAwaitingIcon,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    iconBgColor: 'bg-slate-500',
    description: 'No official VeraScore has been published for this member yet',
    requiresAction: false,
    priority: 1,
  },
  [DashboardStatus.DASHBOARD_READY]: {
    icon: dashboardReadyIcon,
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    iconBgColor: 'bg-green-600',
    description: 'Member dashboard is ready and accessible',
    requiresAction: false,
    priority: 0,
  },
  [DashboardStatus.NEEDS_ATTENTION]: {
    icon: dashboardNeedsAttentionIcon,
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    iconBgColor: 'bg-red-600',
    description: 'Member requires immediate attention from advisor',
    requiresAction: true,
    priority: 5,
  },
  [DashboardStatus.AWAITING_DASHBOARD]: {
    icon: dashboardAwaitingIcon,
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    iconBgColor: 'bg-blue-600',
    description: 'Member completed onboarding, waiting for dashboard setup',
    requiresAction: false,
    priority: 2,
  },
  [DashboardStatus.PENDING]: {
    icon: dashboardPendingIcon,
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    iconBgColor: 'bg-yellow-600',
    description: 'Member status is pending verification or action',
    requiresAction: true,
    priority: 3,
  },
  [DashboardStatus.EMAIL_NOT_CONFIRMED]: {
    icon: dashboardEmailIcon,
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    iconBgColor: 'bg-orange-600',
    description: 'Member needs to confirm their email address',
    requiresAction: true,
    priority: 4,
  },
  [DashboardStatus.IN_PROGRESS]: {
    icon: dashboardInProgressIcon,
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-100',
    iconBgColor: 'bg-indigo-600',
    description: 'Member is actively completing required steps',
    requiresAction: false,
    priority: 1,
  },
  ['Not Set']: {
    icon: dashboardNotSetIcon,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    iconBgColor: 'bg-slate-600',
    description: 'Member status has not been set',
    requiresAction: true,
    priority: 6,
  },
  [DashboardStatus.INVITED]: {
    icon: dashboardPendingIcon,
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    iconBgColor: 'bg-purple-600',
    description: 'Member has been invited but has not yet accepted',
    requiresAction: false,
    priority: 7,
  },
};

// Helper functions
export const getStatusMetadata = (status: string): StatusMetadata => {
  return STATUS_METADATA[status as DashboardStatus] || STATUS_METADATA['Not Set'];
};

export const requiresAction = (status: string): boolean => {
  return getStatusMetadata(status).requiresAction;
};

export const getStatusPriority = (status: string): number => {
  return getStatusMetadata(status).priority;
};

export const sortMembersByStatus = (members: { dashboard_status: string }[]): typeof members => {
  return [...members].sort(
    (a, b) => getStatusPriority(b.dashboard_status) - getStatusPriority(a.dashboard_status)
  );
};
